import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import {
    AccessContextResponse,
    BillingProvider,
    getAccessContext,
    SubscriptionStatus
} from '@/services/accessContext';
import {
    type CapabilityAudiences,
    DEFAULT_CAPABILITY_AUDIENCES,
    type TenantCapability
} from '@/services/tenant_capabilities';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';
import { useUserStore } from '@/stores/user';
import type { OnboardingStatus } from '@/types';

const ACCESS_CONTEXT_STALE_MS = 5 * 60 * 1000; // 5 minutes

export const useAccessContextStore = defineStore('accessContext', () => {
    const authStore = useAuthStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    const billingProvider = ref<BillingProvider>('none');
    const subscriptionStatus = ref<SubscriptionStatus>('pending_payment');
    const onboardingStatus = ref<OnboardingStatus>('pending');

    const onboardingCurrentStep = ref<number | null>(null);
    const loaded = ref(false);
    const loading = ref(false);
    const lastUserId = ref<string>();
    const lastTenantId = ref<string>();
    const lastLoadedAt = ref<number | null>(null);

    const displayFreeTrialBanner = ref<boolean>(false);
    const trialDaysRemaining = ref<number | null>(null);
    const displayGracePeriodBanner = ref<boolean>(false);
    const gracePeriodDaysRemaining = ref<number | null>(null);
    // Defaults to true so an unloaded context never blocks the export button:
    // the backend is the one enforcing the trial limit.
    const canExportSlides = ref<boolean>(true);
    // Seeded with the backend's defaults so a context that never loaded doesn't hide an
    // action every tenant has: the API is the one enforcing.
    const capabilityAudiences = ref<CapabilityAudiences>({
        ...DEFAULT_CAPABILITY_AUDIENCES
    });

    /** Members hold a capability opened to them; admins every one the tenant left to someone. */
    const can = (capability: TenantCapability) => {
        const audience = capabilityAudiences.value[capability];
        if (audience === 'members') return true;
        if (audience === 'nobody') return false;
        return tenantStore.tenantAdmin;
    };

    const closeFreeTrialBanner = () => {
        displayFreeTrialBanner.value = false;
    };
    const closeGracePeriodBanner = () => {
        displayGracePeriodBanner.value = false;
    };

    const hasActiveSubscription = computed<boolean>(() => {
        return (
            subscriptionStatus.value === 'active' ||
            subscriptionStatus.value === 'trialing' ||
            subscriptionStatus.value === 'past_due' ||
            subscriptionStatus.value === 'incomplete'
        );
    });

    const isSubscriptionRequired = computed<boolean>(() => {
        return (
            subscriptionStatus.value === 'unpaid' ||
            subscriptionStatus.value === 'canceled' ||
            subscriptionStatus.value === 'paused' ||
            subscriptionStatus.value === 'incomplete_expired'
        );
    });

    const hasCompletedOnboarding = computed<boolean>(() => {
        return onboardingStatus.value === 'done';
    });

    const isInTrialPeriod = computed<boolean>(() => {
        return subscriptionStatus.value === 'trialing';
    });

    function applyAccessContext(context: AccessContextResponse) {
        billingProvider.value = context.billing_provider;
        subscriptionStatus.value = context.subscription_status;
        onboardingStatus.value = context.onboarding_status;
        onboardingCurrentStep.value = context.onboarding_current_step;
        displayFreeTrialBanner.value = context.display_free_trial_banner;
        trialDaysRemaining.value = context.trial_days_remaining;
        displayGracePeriodBanner.value = context.display_grace_period_banner;
        gracePeriodDaysRemaining.value = context.grace_period_days_remaining;
        canExportSlides.value = context.can_export_slides;
        capabilityAudiences.value = context.capability_audiences;
    }

    function clear() {
        billingProvider.value = 'none';
        subscriptionStatus.value = 'pending_payment';
        onboardingStatus.value = 'pending';
        onboardingCurrentStep.value = 0;
        loaded.value = false;
        loading.value = false;
        lastUserId.value = undefined;
        lastTenantId.value = undefined;
        lastLoadedAt.value = null;
        displayFreeTrialBanner.value = false;
        trialDaysRemaining.value = null;
        displayGracePeriodBanner.value = false;
        gracePeriodDaysRemaining.value = null;
        canExportSlides.value = true;
        capabilityAudiences.value = { ...DEFAULT_CAPABILITY_AUDIENCES };
    }

    function isStale() {
        if (!lastLoadedAt.value) {
            return true;
        }
        return Date.now() - lastLoadedAt.value > ACCESS_CONTEXT_STALE_MS;
    }

    async function ensureLoaded(force = false) {
        const userId = userStore.userId || 'unknown-user';
        const tenantId = tenantStore.tenantId;

        if (!authStore.isAuthenticated || !tenantId) {
            clear();
            return false;
        }
        /* ------------------------------------------------------------
         * Check if access context is still valid
         * ------------------------------------------------------------ */
        if (
            !force &&
            !isStale() &&
            loaded.value &&
            lastUserId.value === userId &&
            lastTenantId.value === tenantId
        ) {
            return true;
        }

        loading.value = true;
        /* ------------------------------------------------------------
         * Load the access context from the backend if it is not valid
         * ------------------------------------------------------------ */
        try {
            const context = await getAccessContext(tenantId);
            applyAccessContext(context);

            loaded.value = true;
            lastUserId.value = userId;
            lastTenantId.value = tenantId;
            lastLoadedAt.value = Date.now();
            return true;
        } catch (error) {
            console.error('Failed to load access context', error);
            if (!loaded.value) {
                clear();
            }
            return false;
        } finally {
            loading.value = false;
        }
    }

    watch(
        () => authStore.isAuthenticated,
        (isAuthenticated) => {
            if (!isAuthenticated) {
                clear();
            }
        }
    );

    return {
        billingProvider,
        subscriptionStatus,
        onboardingStatus,
        onboardingCurrentStep,
        loaded,
        loading,
        hasActiveSubscription,
        hasCompletedOnboarding,
        isSubscriptionRequired,
        displayFreeTrialBanner,
        trialDaysRemaining,
        displayGracePeriodBanner,
        gracePeriodDaysRemaining,
        canExportSlides,
        capabilityAudiences,
        can,
        clear,
        ensureLoaded,
        closeFreeTrialBanner,
        closeGracePeriodBanner,
        isInTrialPeriod
    };
});
